/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useState, type ReactNode } from "react";

import { useFlag } from "@superweb/flags";
import { useLocale } from "@superweb/intl";
import {
  createComboBoxState,
  createRadioGroupState,
  createTextFieldState,
  ExperimentalFormDeprecated,
  useTypo,
  useUiColors,
} from "@superweb/ui";

import { useAction } from "@fleet/api";

import { useLogAction } from "#internal/analytics";
import { Message, useMessage } from "#internal/intl";
import { SubmitBottomSheet, useOperationIdContext } from "#internal/ui";
import {
  useErrorMessages,
  useOnChangePartial,
  useOnShowErrors,
} from "#internal/utils";

import {
  VehicleDetailsFields,
  type VehicleFormState,
} from "./vehicle-details-fields";

export const VehicleDetailsForm = ({
  refId,
  vehicleTypes,
  bottomSheetContent,
  onSubmit,
  onError,
  onSuccess,
}: {
  refId: string;
  vehicleTypes: { id: "car" | "bike" | "rickshaw"; name: string }[];
  bottomSheetContent?: ReactNode;
  onSubmit: () => void;
  onError: (code?: string) => void;
  onSuccess: () => void;
}) => {
  const flag = useFlag();
  const locale = useLocale();
  const logActionSubmit = useLogAction("submit");
  const logActionInvalid = useLogAction("invalid");
  const errorMessages = useErrorMessages();
  const message = useMessage();
  const typo = useTypo();
  const uiColors = useUiColors();

  const { submissionId } = useOperationIdContext();

  const enableVehicleTypesSelection = flag({
    id: "7fd41e03-8f0f-4153-b9ed-a0e613ed5500",
    description: "Enable vehicle types selection",
  });

  const [formState, setFormState] = useState(() => {
    return {
      number: createTextFieldState({
        value: "",
        errorMessage: errorMessages.requiredField,
      }),
      vehicle_type: createRadioGroupState<"car" | "bike" | "rickshaw">({
        value: !enableVehicleTypesSelection
          ? "car"
          : vehicleTypes.length === 1
            ? vehicleTypes[0]?.id
            : undefined,
        errorMessage:
          enableVehicleTypesSelection && vehicleTypes.length > 1
            ? errorMessages.requiredField
            : undefined,
      }),
      brand: createComboBoxState({
        errorMessage: errorMessages.requiredField,
      }),
      model: createComboBoxState({
        errorMessage: errorMessages.requiredField,
      }),
      color_code: createComboBoxState({
        errorMessage: errorMessages.requiredField,
      }),
      year: createComboBoxState({
        errorMessage: errorMessages.requiredField,
      }),
    };
  });

  const onChangePartial = useOnChangePartial(setFormState);
  const onShowErrors = useOnShowErrors(setFormState);

  const { action: actionSubmit, isFetching } = useAction({
    url: "/fleet-forms/selfreg/v1/submit",
    method: "POST",
  });

  return (
    <ExperimentalFormDeprecated
      main={
        <>
          <div
            css={{
              ...typo({
                level: "body2",
                density: "tight",
                weight: "regular",
              }),
              color: uiColors.textMinor,
              marginBlockEnd: "4px",
            }}
          >
            <Message
              id="695eee7f-9c7a-4aca-a06e-b840b761f639"
              context="Yango forms. Vehicle details form. Step description"
              default="You're registered! Add details to start earning right now"
            />
          </div>
          <VehicleDetailsFields
            state={formState}
            onChangePartial={onChangePartial}
            vehicleTypes={vehicleTypes}
            refId={refId}
            enableVehicleTypesSelection={enableVehicleTypesSelection}
          />
          <SubmitBottomSheet
            isLoading={isFetching}
            text={message({
              id: "ca0dfe03-7215-463e-8f07-d1db5c04a303",
              context: "Yango forms. Add info button text",
              default: "Add details",
            })}
          >
            {bottomSheetContent}
          </SubmitBottomSheet>
        </>
      }
      onSubmit={() => {
        onShowErrors();
        const hasErrors = Object.keys(formState).some(
          (field) =>
            formState[field as keyof VehicleFormState].errorMessage !==
            undefined,
        );

        if (hasErrors) {
          if (
            formState.number.errorMessage === errorMessages.invalidCarNumber
          ) {
            logActionInvalid("invalid_car_number", { onlyYM: true });
          }

          return;
        }

        actionSubmit(
          {
            headers: {
              "x-submission-id": submissionId,
              language: locale.toString(),
              "content-type": "application/json",
            },
            query: { ref_id: refId },
            body: {
              car: {
                number: formState.number.value,
                brand: String(formState.brand.value?.key),
                model: String(formState.model.value?.key),
                color_code: String(formState.color_code.value?.key),
                year: Number(formState.year.value?.key),
              },
            },
          },
          {
            onResponse: (res) => {
              if (res.status === 204) {
                onSuccess();
                onSubmit();
                logActionSubmit("submit_vehicle");
                return;
              }

              switch (res.body.code) {
                case "invalid_car_number": {
                  logActionInvalid("invalid_car_number", { onlyYM: true });
                  onChangePartial({
                    number: {
                      ...formState.number,
                      errorMessage: errorMessages.invalidCarNumber,
                      errorVisible: true,
                    },
                  });
                  break;
                }
                default: {
                  onError(res.body.code);
                  break;
                }
              }
            },
          },
        );
      }}
    />
  );
};
